import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { LazyLoadEvent } from 'primeng/api';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { TableItemState } from '../../component-library/table/table-item-state.enum';
import { TableParameters } from '../../component-library/table/table-parameters';
import { BackendPaginationService } from '../../core/backend-pagination.service';
import { BackendService } from '../../core/backend.service';
import { CONFIG } from '../../core/config';
import { DirtyFormStateService } from '../../shared/dirty-form-guard/dirty-form-state.service';
import { GenerateGuidService } from '../../shared/generate-guid/generate-guid.service';
import { TableConfigHelperService } from '../../shared/table-helpers/table-config-helper-service';
import { TableTemplate } from '../../viewmodels/admin/table-attribute-template/table-template';
import { TableTemplateColumn } from '../../viewmodels/admin/table-attribute-template/table-template-column';
import { TableAttributeTemplateTableConfigService } from './table-attribute-template-table-config.service';

@Injectable({
  providedIn: 'root',
})
export class TableAttributeTemplateTableService {
  public selectedAttributes: BehaviorSubject<TableTemplateColumn[]> = new BehaviorSubject<TableTemplateColumn[]>([]);
  public columns: BehaviorSubject<TableTemplateColumn[]> = new BehaviorSubject<TableTemplateColumn[]>([]);
  public tableColumns: TableTemplateColumn;
  public modifiedColumn: TableTemplateColumn[] = [];
  public templateInfo: TableTemplate;
  private unsubscribe: Subject<boolean> = new Subject();

  private canModify: boolean = false;

  public constructor(
    private tableConfigHelperService: TableConfigHelperService,
    private configService: TableAttributeTemplateTableConfigService,
    private generateGuidService: GenerateGuidService,
    private backendPaginationService: BackendPaginationService,
    private backendService: BackendService,
    private dirtyFormStateService: DirtyFormStateService,
  ) { }

  public setParameter(templateInfo: TableTemplate, canModify: boolean): void {
    this.templateInfo = templateInfo;
    this.canModify = canModify;
  }

  public updateDeleteActionButton(attributes: any[]): void {
    this.tableConfigHelperService.updateDeleteAction(this.configService.tableConfig, this.canModify, attributes.length > 0);
  }

  public addNewColumn(column: TableTemplateColumn, maxColumnIndex: number): void {
    column.state = TableItemState.Added;
    column.tableTemplateId = this.templateInfo.tableTemplateId;
    column.refId = this.generateGuidService.newGuid();
    column.columnIndex = maxColumnIndex + 1;
    column.tableTemplateColumnId = this.generateGuidService.newGuid();

    this.modifiedColumn.push(column);
    this.updateColumnList();
    this.dirtyFormStateService.setDirtyStatus(true);
  }

  public updateEditAttributeData(column: TableTemplateColumn): void {
    if (column.state === TableItemState.Unchanged) {
      column.state = TableItemState.Modified;
    }

    column.refId = column.tableTemplateColumnId;

    if (!this.modifiedColumn.find(x => x.refId === column.refId)) {
      this.modifiedColumn.push(column);
    }

    this.updateColumnList();
  }

  private updateColumnList(): void {
    const attributes = this.columns.value.filter(
      x => !this.modifiedColumn.find(attribute => attribute.tableTemplateColumnId === x.tableTemplateColumnId),
    );

    this.columns.next([...this.modifiedColumn.sort((a, b) => a.columnIndex - b.columnIndex), ...attributes]);
    this.togglePagination(false);
    this.dirtyFormStateService.setDirtyStatus(true);
  }

  public clearColumns(): void {
    this.columns.next([]);
    this.selectedAttributes.next([]);
    this.modifiedColumn = [];
  }

  public loadCollections(): void {
    this.selectAttributes([]);
    this.getAttributesTotalRecords();
    this.loadTemplateColumns(this.configService.defaultAttributeLoadEvent);
  }

  public loadTemplateColumns(event: LazyLoadEvent): void {
    const parameters = this.createTableParameter(event);

    this.showTableLoader(true);
    this.togglePagination(true);

    this.loadPaginatedColums(parameters)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(result => {
        if (result) {
          this.modifiedColumn = [];
          this.columns.next([]);
          this.columns.next(result);
          this.showTableLoader(false);
        }
      });
  }

  public getAttributesTotalRecords(): void {
    const params = new TableParameters();

    params.selectedItemId = this.templateInfo.tableTemplateId;
    this.getCount(params)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(count => {
        if (count) {
          this.configService.setTablePaginationConfig(count);
        }
      });
  }

  private getCount(tableParameters: TableParameters): Observable<number> {
    return this.backendPaginationService.getCountWithParameters<TableTemplateColumn>(TableTemplateColumn, tableParameters, false);
  }

  public createTableParameter(event: LazyLoadEvent): TableParameters {
    const parameter = new TableParameters();

    parameter.skip = event.first;
    parameter.take = event.rows;
    parameter.selectedItemId = this.templateInfo.tableTemplateId;

    return parameter;
  }

  private loadPaginatedColums(tableParams: TableParameters): Observable<TableTemplateColumn[]> {
    return this.backendPaginationService.paginateWithParameters<TableTemplateColumn>(
      TableTemplateColumn,
      tableParams,
      tableParams.skip,
      tableParams.take,
      false,
    );
  }

  public loadMaxColumnIndex(): Observable<number> {
    return this.getMaxColumnIndex(this.templateInfo.tableTemplateId).pipe(
      map(maxColumnIndexFromServer => _.max([maxColumnIndexFromServer, ...this.columns.value.map(x => x.columnIndex)])),
    );
  }

  public getMaxColumnIndex(tableTemplateId: string): Observable<number> {
    if (tableTemplateId) {
      return this.backendService.getAnyById(CONFIG.apiRoutes.tableTemplateColumnMaxColumnIndex, tableTemplateId);
    } else {
      return of(0);
    }
  }

  public deleteAttributes(ids: string[]): Observable<boolean> {
    return this.backendService.deleteRange<TableTemplateColumn>(TableTemplateColumn, ids);
  }

  public processDeletedAttributes(columnIdsToDelete: string[]): void {
    const columns = _.cloneDeep(this.columns.value.filter(x => !columnIdsToDelete.includes(x.tableTemplateColumnId)));
    this.modifiedColumn = this.modifiedColumn.filter(x => !columnIdsToDelete.includes(x.tableTemplateColumnId));
    this.columns.next(columns);
    this.selectAttributes([]);
  }

  public selectAttributes(attributes: TableTemplateColumn[]): void {
    this.selectedAttributes.next(attributes);
    this.updateDeleteActionButton(attributes);
  }

  public showTableLoader(showLoader: boolean): void {
    if (this.configService && this.configService.tableConfig) {
      this.configService.tableConfig.loading = showLoader;
    }
  }

  public togglePagination(showPagination: boolean): void {
    if (this.configService && this.configService.tableConfig) {
      this.configService.tableConfig.enablePagination = showPagination;
    }
  }
}
